// src/AppRoot.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import TopQuestionNavigation from "./TopQuestionNavigation";
import SectionResultsViewer from "./SectionResultsViewer";
import { API_BASE, EXEC_APPENDER, BUILD_TIME, APP_NAMES } from "./utils/constants";
import { Shell, PrimaryButton, SecondaryButton, DangerLink, Chip, Skeleton } from "./components/UI";
import { StatusPill } from "./components/StatusPill";
import { CodeBlock } from "./components/CodeBlock";
import { ExamPracticeLogin, NMLSLogin } from "./pages/Login";
import RegistrationPage from "./pages/RegistrationPage";
import { CategoryTable } from "./components/CategoryTable";
import { FlashCardViewer } from "./pages/FlashCardViewer";
import { QuestionWizard } from "./pages/QuestionWizard";
import { ResultsViewer } from "./pages/ResultsViewer";
import { fetchCategorySummary, mapInBatches, fetchQuestionsCount, useDebugRenders, getCategoriesForSection } from "./utils/appHelpers";
import { mapCategoryItem } from "./utils/mappers";
import { fetchTextWithDiagnostics } from "./utils/helpers";
import LoadingScreen from "./components/LoadingScreen";
import AppHeader from "./components/AppHeader";
import SectionsSidebar from "./components/SectionsSidebar";
import QuestionModeToggle from "./components/QuestionModeToggle";
import StaticPage from "./components/StaticPage";


// =====================
// Component: Login Landing Page
// =====================
function LoginLandingPage({ onExamLogin, onNMLSLogin }) {
  const [isDarkMode, setIsDarkMode] = React.useState(
    localStorage.getItem("theme") === "dark"
  );

  React.useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [isDarkMode]);

  const handlePlanSelect = (plan) => {
    localStorage.setItem("selectedPlan", plan);
  };

  const features = [
    "1,000+ Practice Questions (UST)",
    "Expert MLO Instructor Support",
    "User-Friendly On All Devices",
    "50 Additional Math Questions",
    "2026 Rules & Regulations",
    "Review Incorrect Answers Option",
    "Vocabulary Flashcards & Glossary",
    "Study Mode & Timed Exam Mode",
    "1 Week / 1 Month / 3 Months",
    "MLO Exam Tip Videos",
    "Dark/Light Theme Toggle"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Navigation Header */}
      <nav className={isDarkMode ? "bg-slate-900 border-b border-slate-700" : "bg-white border-b border-slate-200"}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img src="/logo-icon.svg" alt="PassMLOExam logo" className="h-10 w-10" />
              <span className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>PassMLOExam</span>
            </div>
            
            <div className="hidden md:flex items-center gap-6 text-sm font-medium">
              <a href="/" className={isDarkMode ? 'text-slate-300 hover:text-blue-400' : 'text-slate-700 hover:text-blue-600'}>Home</a>
              <a href="/faq.html" className={isDarkMode ? 'text-slate-300 hover:text-blue-400' : 'text-slate-700 hover:text-blue-600'}>FAQ</a>
              <button onClick={onNMLSLogin} className={isDarkMode ? 'text-slate-300 hover:text-blue-400' : 'text-slate-700 hover:text-blue-600'}>NMLS Test</button>
              <a href="/resources/index.html" className={isDarkMode ? 'text-slate-300 hover:text-blue-400' : 'text-slate-700 hover:text-blue-600'}>Study Guides</a>
              <a href="/mortgage-calculator.html" className={isDarkMode ? 'text-slate-300 hover:text-blue-400' : 'text-slate-700 hover:text-blue-600'}>Calculator</a>
              <a href="#" className={isDarkMode ? 'text-slate-300 hover:text-blue-400' : 'text-slate-700 hover:text-blue-600'}>Private Tutoring</a>
              <a href="/resources/index.html" className={isDarkMode ? 'text-slate-300 hover:text-blue-400' : 'text-slate-700 hover:text-blue-600'}>Blog</a>
              <a href="/contact.html" className={isDarkMode ? 'text-slate-300 hover:text-blue-400' : 'text-slate-700 hover:text-blue-600'}>Contact Us</a>
              <div className="relative group">
                <button className={isDarkMode ? 'text-slate-300 hover:text-blue-400' : 'text-slate-700 hover:text-blue-600'} onClick={() => setIsDarkMode(!isDarkMode)}>
                  {isDarkMode ? '🌙' : '☀️'}
                </button>
              </div>
              {/* <button onClick={onNMLSLogin} className={isDarkMode ? 'text-slate-300 hover:text-blue-400' : 'text-slate-700 hover:text-blue-600'}>Logout</button> */}
              <button onClick={onNMLSLogin} className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition font-semibold">
                Sign In
              </button>
            </div>

            <button className="md:hidden" onClick={onNMLSLogin}>
              <svg className="w-6 h-6 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className={`min-h-screen ${isDarkMode ? 'bg-slate-900' : 'bg-gradient-to-br from-blue-50 to-indigo-50'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Left Column - Text Content */}
          <div>
            <h1 className={`text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-2`}>
              Online NMLS Test Prep (UST)
            </h1>
            <h2 className={`text-3xl font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'} mb-6`}>
              NMLS Practice Test Questions
            </h2>
            
            <div className={`${isDarkMode ? 'text-slate-300' : 'text-slate-700'} leading-relaxed space-y-4 mb-8`}>
              <p>
                Pass the NMLS test! All of our NMLS practice test questions are 
                carefully crafted by an expert mortgage instructor to meet or 
                exceed the difficulty level of the actual NMLS exam. Don't rely on 
                the outdated material on other NMLS test prep sites! All of our SAFE 
                MLO exam questions are up to date with the latest 2026 rules and 
                regulations and are guaranteed to help you pass your MLO test.
              </p>
              
              <div className="my-6">
                <button 
                  onClick={onNMLSLogin}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-bold text-lg transition shadow-lg hover:shadow-xl"
                >
                  Click HERE To Pass The NMLS Test
                </button>
              </div>
              
              <p>
                Our mortgage exam prep has helped thousands of NMLS test-takers 
                pass their mortgage test on the 1st try. We are totally confident 
                that our SAFE MLO test prep will help you pass your mortgage loan 
                originator exam. we offer a 100% Pass Money-Back Guarantee!
              </p>
            </div>
          </div>

          {/* Right Column - Features Box */}
          <div>
            <div className={`${isDarkMode ? 'bg-slate-800' : 'bg-white'} rounded-2xl shadow-xl overflow-hidden`}>
              <div className="bg-blue-600 text-white text-center py-6 px-4">
                <h3 className="text-2xl font-bold">
                  OUR SAFE MLO EXAM PREP INCLUDES...
                </h3>
              </div>
              
              <div className="p-6 grid grid-cols-1 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-green-500 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className={`${isDarkMode ? 'text-slate-300' : 'text-slate-700'} ${index === 11 ? `font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}` : ''}`}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>

              {/* Pricing Buttons */}
              <div className="p-6 pt-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  onClick={() => {
                    handlePlanSelect('monthly');
                    window.location.hash = '#/register?plan=monthly';
                  }}
                  className="block bg-orange-500 hover:bg-orange-600 text-white text-center px-6 py-4 rounded-lg font-bold transition shadow-md hover:shadow-lg"
                >
                  <div className="text-sm">1 Month Access</div>
                  <div className="text-2xl font-bold">$20</div>
                </button>
                <button
                  onClick={() => {
                    handlePlanSelect('quarterly');
                    window.location.hash = '#/register?plan=quarterly';
                  }}
                  className="block bg-orange-500 hover:bg-orange-600 text-white text-center px-6 py-4 rounded-lg font-bold transition shadow-md hover:shadow-lg"
                >
                  <div className="text-sm">3 Month Access</div>
                  <div className="text-2xl font-bold">$40</div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Footer */}
      
      <footer className={`mt-16 border-t ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} text-center space-y-2`}>
            <div>
              © 2026 Exam Practice App. All rights reserved.
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
              <a href="/faq.html" className={isDarkMode ? 'hover:text-slate-300' : 'hover:text-slate-700'}>FAQ</a>
              <a href="/contact.html" className={isDarkMode ? 'hover:text-slate-300' : 'hover:text-slate-700'}>Contact Us</a>
              <a href="/privacy.html" className={isDarkMode ? 'hover:text-slate-300' : 'hover:text-slate-700'}>Privacy</a>
              <a href="/copyright.html" className={isDarkMode ? 'hover:text-slate-300' : 'hover:text-slate-700'}>Copyright</a>
              <a href="/resources/index.html" className={isDarkMode ? 'hover:text-slate-300' : 'hover:text-slate-700'}>Study Guides</a>
              <a href="/mortgage-calculator.html" className={isDarkMode ? 'hover:text-slate-300' : 'hover:text-slate-700'}>Mortgage Calculator</a>
              <a href="https://buydeepdiscount.com" className={isDarkMode ? 'hover:text-slate-300' : 'hover:text-slate-700'} target="_blank" rel="noreferrer">
                BuyDeep
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function getHashRoute() {
  const raw = window.location.hash || "";
  // Split on ? to separate route and query
  const [routePart, queryString] = raw.replace(/^#\//, "").split("?");
  return routePart ? routePart.trim() : "";
}

function getHashQuery() {
  const raw = window.location.hash || "";
  const [, queryString] = raw.replace(/^#\//, "").split("?");
  if (!queryString) return {};
  return Object.fromEntries(new URLSearchParams(queryString));
}

// StaticPage is now in its own component file

// =====================
// App root
// =====================
export default function AppRoot() {
  const [token, setToken] = useState(localStorage.getItem("accessToken") || "");
  const [view, setView] = useState(null);
  const [loginMode, setLoginMode] = useState(localStorage.getItem("loginMode") || null); // null = landing, "exam" = exam, "nmls" = nmls
  const [publicRoute, setPublicRoute] = useState(getHashRoute());
  const [appName, setAppName] = useState(localStorage.getItem("appName") || APP_NAMES[0]);
  const [questionMode, setQuestionMode] = useState(
    localStorage.getItem("questionMode") || "practice"
  );

  // Track dark mode state
  const [isDarkMode, setIsDarkMode] = useState(localStorage.getItem("theme") === "dark");

  // Initialize and sync dark mode
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
      document.body.style.backgroundColor = "#0f172a";
      document.body.style.color = "#f1f5f9";
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      document.body.style.backgroundColor = "#f8fafc";
      document.body.style.color = "#1e293b";
      localStorage.setItem("theme", "light");
    }
  }, [isDarkMode]);

  // Update appName whenever token changes (user logs in/out)
  useEffect(() => {
    const newAppName = localStorage.getItem("appName") || APP_NAMES[0];
    setAppName(newAppName);
    console.log('🔄 appName updated from localStorage:', newAppName);
  }, [token]);

  const [sections, setSections] = useState([]);
  const [catMap, setCatMap] = useState({});
  const [activeSection, setActiveSection] = useState("");
  const [loadingData, setLoadingData] = useState(false);

  const [catSummaries, setCatSummaries] = useState({});
  const [loadingSummaries, setLoadingSummaries] = useState(false);

  const [sectionTotals, setSectionTotals] = useState({});
  const [loadingSectionTotals, setLoadingSectionTotals] = useState(false);

  const allCategoriesRef = useRef([]);
  const sectionDetailsRef = useRef(null);

  // Debug renders - ONLY track actual state, not unused category stuff
  const debugState = useMemo(() => ({
    token, view, questionMode, sections, activeSection, loadingData
  }), [token, view, questionMode, sections, activeSection, loadingData]);
  
  // useDebugRenders(debugState);  // TEMPORARILY DISABLED TO TEST

  const logout = useCallback(() => {
    console.log('🔴 LOGOUT CALLED');
    localStorage.removeItem("accessToken");
    localStorage.removeItem("appName");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("loginMode");
    setToken("");
    setLoginMode(null);
    setView(null);
  }, []);

  useEffect(() => {
    localStorage.setItem("questionMode", questionMode);
  }, [questionMode]);

  useEffect(() => {
    const onHashChange = () => setPublicRoute(getHashRoute());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    console.log('🟢 useEffect [token, appName] RUNNING');
    if (!token) {
      console.log('🟢 useEffect: no token, returning');
      return;
    }
    
    async function load() {
      console.log('🟠 load() CALLED');
      console.log('🟠 calling setLoadingData(true)');
      setLoadingData(true);
      
      const secRes = await fetchTextWithDiagnostics(
        `${API_BASE}/examssections${EXEC_APPENDER}/${encodeURIComponent(appName)}`,
        { headers: { Authorization: token } }
      );
      
      if (secRes.status === 401) {
        setLoadingData(false);
        localStorage.removeItem("accessToken");
        localStorage.removeItem("appName");
        localStorage.removeItem("userEmail");
        setToken("");
        setView(null);
        return;
      }

      let secList = [];
      try {
        secList = JSON.parse(secRes.text) || [];
      } catch {}

      console.log('🟠 calling setSections, length:', secList.length);
      setSections(secList);
      console.log('🟠 calling setActiveSection:', secList?.[0] || "");
      setActiveSection(secList?.[0] || "");
      console.log('🟠 calling setLoadingData(false)');
      setLoadingData(false);
    }

    console.log('🟢 calling load()');
    load();
  }, [token, appName]);

  // Load categories bulk (by app, returns map of all sections)
  useEffect(() => {
    console.log('🟢 useEffect [token, appName] - loading all categories');
    if (!token) {
      console.log('🟢 No token, skipping categories load');
      return;
    }

    async function loadCategories() {
      try {
        const url = `${API_BASE}/examscategories${EXEC_APPENDER}/${encodeURIComponent(appName)}`;
        console.log('🟠 Fetching all categories from:', url);
        
        const res = await fetchTextWithDiagnostics(url, {
          headers: { Authorization: token }
        });

        if (res.status === 401) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("appName");
          localStorage.removeItem("userEmail");
          setToken("");
          setView(null);
          return;
        }

        if (!res.ok) {
          console.log('🟠 Failed to load categories:', res.status);
          setCatMap({});
          return;
        }

        try {
          const catMapData = JSON.parse(res.text) || {};
          console.log('🟠 All categories loaded - sections:', Object.keys(catMapData).length);
          setCatMap(catMapData);
        } catch (e) {
          console.log('🟠 Failed to parse categories:', e);
          setCatMap({});
        }
      } catch (e) {
        console.log('🟠 Error loading categories:', e);
        setCatMap({});
      }
    }

    loadCategories();
  }, [token, appName]);

  const onPrev = useCallback(() => {
    setView((v) => {
      if (!v || v.type !== "questions") return v;
      const list = v.categoryList || [];
      const catIdx = v.categoryIndex || 0;
      const qIdx = v.questionIndex || 0;

      if (qIdx > 0) return { ...v, questionIndex: qIdx - 1 };
      if (catIdx > 0) {
        const prevCatIdx = catIdx - 1;
        return {
          ...v,
          categoryIndex: prevCatIdx,
          category: list[prevCatIdx] || v.category,
          questionIndex: 0,
          totalQuestions: 0,
        };
      }
      return v;
    });
  }, []);

  const onNext = useCallback(() => {
    setView((v) => {
      if (!v || v.type !== "questions") return v;
      const list = v.categoryList || [];
      const catIdx = v.categoryIndex || 0;
      const qIdx = v.questionIndex || 0;
      const total = v.totalQuestions || 0;

      if (total > 0 && qIdx < total - 1)
        return { ...v, questionIndex: qIdx + 1 };
      if (catIdx < list.length - 1) {
        const nextCatIdx = catIdx + 1;
        return {
          ...v,
          categoryIndex: nextCatIdx,
          category: list[nextCatIdx] || v.category,
          questionIndex: 0,
          totalQuestions: 0,
        };
      }
      return v;
    });
  }, []);

  // IMPORTANT: All hooks MUST be called before any early returns!
  const rightItems = useMemo(() => getCategoriesForSection(catMap, activeSection), [catMap, activeSection]);
  
  useEffect(() => {
    console.log('allCategoriesRef useEffect - rightItems.length:', rightItems.length);
    console.log('🔍 rightItems:', rightItems);
    allCategoriesRef.current = rightItems.map((x) => x.label);
    console.log('🔍 allCategoriesRef.current:', allCategoriesRef.current);
  }, [rightItems]);
  
  const openCategoriesHome = useCallback(() => setView(null), []);

  const refreshSummaries = useCallback(async () => {
    if (!token || !sections?.length) return;
    setLoadingSummaries(true);
    try {
      let allResults = {};
      // Fetch summaries for each section asynchronously
      await Promise.all(
        sections.map(async (section) => {
          const cats = (catMap?.[section] || [])
            .map(mapCategoryItem)
            .map((x) => x.label);
          if (cats.length === 0) return;
          const results = await mapInBatches(cats, 6, async (cat) =>
            fetchCategorySummary({
              token,
              appName,
              section,
              category: cat,
              onUnauthorized: logout,
            })
          );
          // Update catSummaries as each section finishes
          for (const r of results) {
            allResults[r.category] = r;
          }
          setCatSummaries((prev) => ({ ...prev, ...allResults }));
        })
      );
    } finally {
      setLoadingSummaries(false);
    }
  }, [token, sections, appName, catMap, logout]);

  const refreshSectionTotals = useCallback(async () => {
    if (!token || !activeSection) return;
    if (typeof sectionTotals?.[activeSection] === "number") return;

    const cats = (catMap?.[activeSection] || [])
      .map(mapCategoryItem)
      .map((x) => x.label);
    if (cats.length === 0) {
      setSectionTotals((prev) => ({ ...prev, [activeSection]: 0 }));
      return;
    }

    setLoadingSectionTotals(true);
    try {
      const counts = await mapInBatches(cats, 4, async (cat) =>
        fetchQuestionsCount({
          token,
          appName,
          section: activeSection,
          category: cat,
          onUnauthorized: () => {
            localStorage.removeItem("accessToken");
            localStorage.removeItem("appName");
            localStorage.removeItem("userEmail");
            setToken("");
            setView(null);
          },
        })
      );
      const total = counts.reduce((sum, n) => sum + (Number(n) || 0), 0);
      setSectionTotals((prev) => ({ ...prev, [activeSection]: total }));
    } finally {
      setLoadingSectionTotals(false);
    }
  }, [token, activeSection, appName, catMap, sectionTotals]);

  // Auto-load summaries when section changes
  useEffect(() => {
    if (!token || !activeSection) return;
    // Get all categories for the active section
    const cats = (catMap?.[activeSection] || []).map(mapCategoryItem).map((x) => x.label);
    // Check if all summaries for these categories are already cached
    const allCached = cats.length > 0 && cats.every(cat => catSummaries[cat]);
    if (allCached) return;
    console.log('🟢 Auto-loading summaries for:', activeSection);
    refreshSummaries();
  }, [token, activeSection, appName, catMap, refreshSummaries, catSummaries]);

  // Auto-load section totals when section changes
  useEffect(() => {
    if (!token || !activeSection) return;
    console.log('🟢 Auto-loading section totals for:', activeSection);
    refreshSectionTotals();
  }, [token, activeSection, appName, catMap, refreshSectionTotals]);

  const handlePractice = useCallback((cat, filter) => {
    setView({
      type: "questions",
      section: activeSection,
      category: cat,
      categoryList: allCategoriesRef.current,
      categoryIndex: cat ? allCategoriesRef.current.indexOf(cat) : 0,
      questionIndex: 0,
      totalQuestions: 0,
      filter: filter,
    });
  }, [activeSection]);

  const handleFlash = useCallback((cat) => {
    setView({
      type: "flash",
      section: activeSection,
      category: cat,
    });
  }, [activeSection]);

  const handleResults = useCallback((cat) => {
    setView({
      type: "results",
      section: activeSection,
      category: cat,
    });
  }, [activeSection]);

  // NOW we can do early returns (after all hooks are called)
  if (!token) {
    if (publicRoute === "register") {
      const query = getHashQuery();
      const plan = query.plan === "quarterly" ? "quarterly" : "monthly";
      return <RegistrationPage plan={plan} onBack={() => { window.location.hash = "#/"; }} />;
    }
    if (publicRoute) {
      return <StaticPage route={publicRoute} onBack={() => { window.location.hash = "#/"; }} />;
    }
    if (!loginMode) {
      return (
        <LoginLandingPage
          onExamLogin={() => setLoginMode("exam")}
          onNMLSLogin={() => setLoginMode("nmls")}
        />
      );
    }

    if (loginMode === "exam") {
      return (
        <ExamPracticeLogin 
          onLogin={(token) => {
            localStorage.setItem("loginMode", "exam");
            setToken(token);
          }}
        />
      );
    }

    if (loginMode === "nmls") {
      return (
        <NMLSLogin 
          onLogin={(token) => {
            localStorage.setItem("loginMode", "nmls");
            setToken(token);
          }}
        />
      );
    }
  }

  if (loadingData) return <LoadingScreen />;

  return (
    <Shell>
      {!!token && (
        <AppHeader 
          appName={appName} 
          onLogout={logout} 
          isDarkMode={isDarkMode} 
          setIsDarkMode={setIsDarkMode}
          showLogout={true}
        />
      )}

      <div className={`flex flex-col lg:flex-row min-h-[calc(100vh-64px)] ${isDarkMode ? 'bg-slate-950' : 'bg-white'}`}>
        <SectionsSidebar
          sections={sections}
          activeSection={activeSection}
          sectionTotals={sectionTotals}
          loadingSectionTotals={loadingSectionTotals}
          isDarkMode={isDarkMode}
          catMap={catMap}
          catSummaries={catSummaries}
          onSectionClick={(sec) => {
            setActiveSection(sec);
            setView(null);
            if (typeof window !== "undefined" && window.innerWidth < 1024) {
              window.requestAnimationFrame(() => {
                sectionDetailsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              });
            }
          }}
        />

        <main className={`flex-1 min-w-0 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
          {!view && (
            <div ref={sectionDetailsRef} className="p-4 sm:p-5">
              <div className={`rounded-3xl border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'} shadow-sm`}>
                <div className={`px-4 py-4 sm:px-6 sm:py-5 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className={`text-lg sm:text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {activeSection || "Select a section"}
                      </div>
                      <div className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} mt-1`}>
                        Categories with Pass/Fail/Unanswered status
                      </div>
                    </div>

                    <div className="w-full sm:w-auto flex items-center gap-2 flex-nowrap overflow-x-auto pb-1">
                      <Chip className="shrink-0">{rightItems.length} categories</Chip>

                      <Chip className="shrink-0 bg-indigo-50 text-indigo-700 border-indigo-200">
                        {typeof sectionTotals?.[activeSection] === "number"
                          ? `Total Questions: ${sectionTotals[activeSection]}`
                          : "Total Questions: —"}
                      </Chip>

                      <SecondaryButton
                        onClick={() =>
                          setView({
                            type: "sectionResults",
                            section: activeSection,
                            categories: allCategoriesRef.current,
                          })
                        }
                        disabled={
                          !activeSection || allCategoriesRef.current.length === 0
                        }
                        className="shrink-0 px-3 py-1.5 text-xs sm:px-4 sm:py-2 sm:text-sm"
                        isDarkMode={isDarkMode}
                      >
                        Section Results
                      </SecondaryButton>

                      <SecondaryButton
                        onClick={() => {
                          setSectionTotals((prev) => {
                            const next = { ...prev };
                            delete next[activeSection];
                            return next;
                          });
                          refreshSectionTotals();
                        }}
                        disabled={
                          loadingSectionTotals ||
                          !activeSection ||
                          allCategoriesRef.current.length === 0
                        }
                        className="shrink-0 px-3 py-1.5 text-xs sm:px-4 sm:py-2 sm:text-sm"
                        isDarkMode={isDarkMode}
                      >
                        {loadingSectionTotals ? "Counting…" : "Refresh totals"}
                      </SecondaryButton>

                      <QuestionModeToggle mode={questionMode} onChange={setQuestionMode} isDarkMode={isDarkMode} />
                    </div>
                  </div>
                </div>

                {rightItems.length === 0 ? (
                  <div className="px-6 py-6">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-700">
                      No categories found for this section.
                    </div>
                  </div>
                ) : (
                  <CategoryTable
                    items={rightItems}
                    summaries={catSummaries}
                    loadingSummaries={loadingSummaries}
                    onRefreshSummaries={refreshSummaries}
                    onPractice={handlePractice}
                    onFlash={handleFlash}
                    onResults={handleResults}
                    isDarkMode={isDarkMode}
                  />
                )}
              </div>
            </div>
          )}

          {view?.type === "flash" && (
            <FlashCardViewer
              token={token}
              appName={appName}
              section={view.section}
              category={view.category}
              onBack={openCategoriesHome}
              onLogout={logout}
              onUnauthorized={logout}
            />
          )}

          {view?.type === "sectionResults" && (
            <SectionResultsViewer
              token={token}
              appName={appName}
              section={view.section}
              categories={view.categories || []}
              apiBase={API_BASE}
              execAppender={EXEC_APPENDER}
              onBack={openCategoriesHome}
              onUnauthorized={logout}
              onOpenCategoryResults={(cat) =>
                setView({
                  type: "results",
                  section: view.section,
                  category: cat,
                })
              }
              isDarkMode={isDarkMode}
            />
          )}

          {view?.type === "results" && (
            <ResultsViewer
              token={token}
              appName={appName}
              section={view.section}
              category={view.category}
              onBack={() => {
                setView(null);
                refreshSummaries();
              }}
              onUnauthorized={logout}
            />
          )}

          {view?.type === "questions" && (
            <div className="p-5">
              <div className="mb-4">
                <TopQuestionNavigation
                  appName={appName}
                  section={view.section}
                  category={view.category}
                  categoryList={view.categoryList || []}
                  categoryIndex={view.categoryIndex || 0}
                  questionIndex={view.questionIndex || 0}
                  totalQuestions={view.totalQuestions || 0}
                  onBackToCategories={() => {
                    setView(null);
                    refreshSummaries();
                  }}
                  onPrev={onPrev}
                  onNext={onNext}
                  onNavigateCategory={(idx) => {
                    const list = view.categoryList || [];
                    const nextCat = list[idx];
                    if (!nextCat) return;
                    setView({
                      ...view,
                      categoryIndex: idx,
                      category: nextCat,
                      questionIndex: 0,
                      totalQuestions: 0,
                    });
                  }}
                  isDarkMode={isDarkMode}
                />
              </div>

              <QuestionWizard
                token={token}
                appName={appName}
                section={view.section}
                category={view.category}
                categoryList={view.categoryList || []}
                categoryIndex={view.categoryIndex || 0}
                filter={view.filter}
                mode={questionMode}
                onUnauthorized={logout}
                onLogout={logout}
                onBack={() => {
                  setView(null);
                  refreshSummaries();
                }}
                onNavigateCategory={(idx) => {
                  // If in filtered mode (practicing wrong/unanswered), just go back
                  if (!view.category) {
                    setView(null);
                    refreshSummaries();
                    return;
                  }
                  
                  const list = view.categoryList || [];
                  const nextCat = list[idx];
                  if (!nextCat) {
                    // End of categories, go back
                    setView(null);
                    refreshSummaries();
                    return;
                  }

                  setView({
                    ...view,
                    categoryIndex: idx,
                    category: nextCat,
                    questionIndex: 0,
                    totalQuestions: 0,
                  });
                }}
                isDarkMode={isDarkMode}
              />
            </div>
          )}
        </main>
      </div>
    </Shell>
  );
}

